import * as Sentry from '@sentry/react';
import { errorLogger } from './errorLogger';

export function setupGlobalErrorHandlers() {
  // Handle unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    const sentryClient = Sentry.getClient();
    if (sentryClient) {
      Sentry.captureException(event.error || new Error(event.message), {
        tags: { source: 'window.onerror' },
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    }

    errorLogger.logError(event.error || new Error(event.message), undefined, {
      category: 'unknown',
      metadata: {
        source: 'window.onerror',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

    const sentryClient = Sentry.getClient();
    if (sentryClient) {
      Sentry.captureException(error, {
        tags: { source: 'unhandledrejection' },
      });
    }

    errorLogger.logError(error, undefined, {
      category: 'unknown',
      metadata: {
        source: 'unhandledrejection',
        reason: event.reason,
      },
    });

    // Prevent the default console error
    event.preventDefault();
  });

  // Handle resource loading errors
  window.addEventListener(
    'error',
    (event) => {
      if (event.target !== window) {
        const target = event.target as HTMLElement;
        errorLogger.logError(
          new Error(
            `Failed to load ${target.tagName.toLowerCase()}: ${target.getAttribute('src') || target.getAttribute('href')}`
          ),
          undefined,
          {
            category: 'network',
            metadata: {
              source: 'resource_error',
              element: target.tagName.toLowerCase(),
              sourceUrl: target.getAttribute('src') || target.getAttribute('href'),
            },
          }
        );
      }
    },
    true
  );

  // Log application lifecycle events
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      errorLogger.logInfo('Application hidden', 'unknown', {
        event: 'visibilitychange',
        state: 'hidden',
      });
    } else {
      errorLogger.logInfo('Application visible', 'unknown', {
        event: 'visibilitychange',
        state: 'visible',
      });
    }
  });

  // Log online/offline status changes
  window.addEventListener('online', () => {
    errorLogger.logInfo('Connection restored', 'network', {
      onlineStatus: true,
    });
  });

  window.addEventListener('offline', () => {
    errorLogger.logWarning('Connection lost', 'network', {
      onlineStatus: false,
    });
  });

  // Log performance metrics periodically (in production only)
  if (!import.meta.env.DEV) {
    setInterval(() => {
      try {
        if ('memory' in performance) {
          const memoryPerformance = performance as {
            memory: {
              usedJSHeapSize: number;
              jsHeapSizeLimit: number;
              totalJSHeapSize: number;
            };
          };
          const memory = memoryPerformance.memory;
          const memoryUsageMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);

          // Log warning if memory usage is high
          if (memoryUsageMB > 100) {
            errorLogger.logWarning(`High memory usage detected: ${memoryUsageMB}MB`, 'unknown', {
              memoryUsage: memoryUsageMB,
              memoryLimit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
              totalHeapSize: Math.round(memory.totalJSHeapSize / 1024 / 1024),
            });
          }
        }

        // Check for long tasks
        if ('PerformanceObserver' in window) {
          const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (entry.duration > 50) {
                // Long tasks > 50ms
                errorLogger.logWarning(
                  `Long task detected: ${entry.duration.toFixed(2)}ms`,
                  'unknown',
                  {
                    taskDuration: entry.duration,
                    taskName: entry.name,
                    startTime: entry.startTime,
                  }
                );
              }
            }
          });

          observer.observe({ entryTypes: ['longtask'] });
        }
      } catch {
        // Silently ignore monitoring errors
      }
    }, 30000); // Check every 30 seconds
  }
}
